import React from 'react';
import styled from 'styled-components';
import { Bot, Edit, Save, Trash2, Plus, X, Check } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const TableContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
`;

const Badge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.bg || props.theme.colors.background};
  color: ${props => props.color || props.theme.colors.text};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.bg || props.theme.colors.primary};
  color: white;
  cursor: pointer;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    opacity: 0.9;
  }
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.xs};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  width: 100%;
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.xs};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  width: 100%;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 24px;
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  input:checked + .slider {
    background-color: ${props => props.theme.colors.primary};
  }
  input:checked + .slider:before {
    transform: translateX(20px);
  }
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

function BotActions() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [editData, setEditData] = React.useState({});

  // Загрузка действий бота и провайдеров
  const { data: actionsData, isLoading: actionsLoading } = useQuery(
    'bot-actions',
    () => adminAPI.getBotActions({ params: { lang: 'ru' } }),
    {
      refetchInterval: 30000, // Обновление каждые 30 секунд
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 10000),
      staleTime: 10000,
      cacheTime: 300000,
    }
  );

  const { data: providersData } = useQuery(
    'providers',
    () => adminAPI.getProviders(),
    {
      enabled: !actionsLoading,
    }
  );

  const updateMutation = useMutation(
    ({ actionId, data: updateData }) => adminAPI.updateBotAction(actionId, updateData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bot-actions');
        setEditingId(null);
        setEditData({});
        toast.success('Действие обновлено');
      },
      onError: (err) => {
        toast.error(`Ошибка: ${err.response?.data?.detail || err.message || 'Не удалось обновить действие'}`);
      }
    }
  );

  const createMutation = useMutation(
    (data) => adminAPI.createBotAction(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bot-actions');
        setCreating(false);
        setEditData({});
        toast.success('Действие создано');
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail || err.message || 'Не удалось создать действие';
        // Показываем понятное сообщение для распространенных ошибок
        if (errorDetail.includes('already exists')) {
          toast.error(`Действие с ключом "${editData.action_key}" уже существует. Используйте другой ключ или отредактируйте существующее действие.`);
        } else {
          toast.error(`Ошибка: ${errorDetail}`);
        }
      }
    }
  );

  const deleteMutation = useMutation(
    (actionId) => adminAPI.deleteBotAction(actionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bot-actions');
        toast.success('Действие удалено');
      },
      onError: (err) => {
        toast.error(`Ошибка: ${err.response?.data?.detail || err.message || 'Не удалось удалить действие'}`);
      }
    }
  );

  const handleEdit = (action) => {
    setEditingId(action.id);
    setEditData({
      display_name_ru: action.display_name,
      display_name_en: action.display_name_en || '',
      provider_id: action.provider_id || null,
      enabled: action.enabled,
      priority: action.priority || 0,
    });
  };

  const handleCreate = () => {
    setCreating(true);
    setEditData({
      action_key: '',
      display_name_ru: '',
      display_name_en: '',
      type: 'image',
      provider_id: null,
      enabled: true,
      priority: 0,
    });
  };

  const handleSave = (actionId) => {
    if (actionId === 'new') {
      if (!editData.action_key || !editData.display_name_ru) {
        toast.error('Укажите ключ действия и название');
        return;
      }
      
      // Проверка на существующий ключ
      const existingAction = actions.find(a => a.action_key === editData.action_key);
      if (existingAction) {
        toast.error(`Действие с ключом "${editData.action_key}" уже существует (ID: ${existingAction.id}). Используйте другой ключ или отредактируйте существующее.`);
        return;
      }
      
      createMutation.mutate(editData);
    } else {
      updateMutation.mutate({ actionId, data: editData });
    }
  };

  const handleDelete = (actionId, actionKey) => {
    if (window.confirm(`Вы уверены, что хотите удалить действие "${actionKey}"?`)) {
      deleteMutation.mutate(actionId);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setCreating(false);
    setEditData({});
  };

  const getProvidersByType = (type) => {
    if (!providersData?.data?.providers) return [];
    return providersData.data.providers.filter(p => p.type === type && p.enabled);
  };

  if (actionsLoading) {
    return (
      <Container>
        <LoadingState>Загрузка действий бота...</LoadingState>
      </Container>
    );
  }

  const actions = actionsData?.actions || actionsData?.data?.actions || [];
  const providers = providersData?.data?.providers || [];
  
  // Получаем список существующих ключей для валидации
  const existingKeys = new Set(actions.map(a => a.action_key));

  return (
    <Container>
      <Header>
        <Title>
          <Bot size={32} />
          Действия бота
        </Title>
        <Button bg="#4f46e5" onClick={handleCreate}>
          <Plus size={16} />
          Создать действие
        </Button>
      </Header>

      {creating ? (
        <TableContainer>
          <div style={{ padding: '24px' }}>
            <h2 style={{ marginBottom: '24px', color: '#1f2937' }}>Создать новое действие</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '900px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Ключ действия * (image_gen, video_gen, text_chatgpt)
                </label>
                <Input
                  type="text"
                  placeholder="image_gen"
                  value={editData.action_key || ''}
                  onChange={(e) => setEditData({...editData, action_key: e.target.value})}
                />
                {editData.action_key && existingKeys.has(editData.action_key) && (
                  <div style={{ marginTop: '4px', color: '#dc2626', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>⚠️</span>
                    <span>Действие с таким ключом уже существует. Используйте другой ключ или отредактируйте существующее действие.</span>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Тип *
                </label>
                <Select
                  value={editData.type || 'image'}
                  onChange={(e) => setEditData({...editData, type: e.target.value})}
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="text">Text</option>
                  <option value="faceswap">FaceSwap</option>
                </Select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Название (RU) *
                </label>
                <Input
                  type="text"
                  placeholder="Работа с изображением"
                  value={editData.display_name_ru || ''}
                  onChange={(e) => setEditData({...editData, display_name_ru: e.target.value})}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Название (EN)
                </label>
                <Input
                  type="text"
                  placeholder="Image generation"
                  value={editData.display_name_en || ''}
                  onChange={(e) => setEditData({...editData, display_name_en: e.target.value})}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Провайдер
                </label>
                <Select
                  value={editData.provider_id || ''}
                  onChange={(e) => setEditData({...editData, provider_id: e.target.value ? parseInt(e.target.value) : null})}
                >
                  <option value="">-- Не привязан --</option>
                  {getProvidersByType(editData.type || 'image').map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.type})</option>
                  ))}
                </Select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                  Приоритет
                </label>
                <Input
                  type="number"
                  value={editData.priority || 0}
                  onChange={(e) => setEditData({...editData, priority: parseInt(e.target.value) || 0})}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', gridColumn: '1 / -1' }}>
                <ToggleSwitch>
                  <input
                    type="checkbox"
                    checked={editData.enabled !== undefined ? editData.enabled : true}
                    onChange={(e) => setEditData({...editData, enabled: e.target.checked})}
                  />
                  <span className="slider" />
                </ToggleSwitch>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Включен</span>
              </div>
              <div style={{ display: 'flex', gap: '12px', gridColumn: '1 / -1', marginTop: '8px' }}>
                <Button bg="#059669" onClick={() => handleSave('new')}>
                  <Save size={14} />
                  Создать
                </Button>
                <Button bg="#6b7280" onClick={handleCancel}>
                  Отмена
                </Button>
              </div>
            </div>
          </div>
        </TableContainer>
      ) : actions.length === 0 ? (
        <EmptyState>
          <div style={{ marginBottom: '16px' }}>Действий бота не найдено</div>
          <Button bg="#4f46e5" onClick={handleCreate}>
            <Plus size={16} />
            Создать первое действие
          </Button>
        </EmptyState>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <Th>Ключ</Th>
                  <Th>Название</Th>
                  <Th>Тип</Th>
                  <Th>Провайдер</Th>
                  <Th>Приоритет</Th>
                  <Th>Статус</Th>
                  <Th>Действия</Th>
                </tr>
              </TableHeader>
              <tbody>
                {actions.map((action) => (
                  editingId === action.id ? (
                    <tr key={`edit-${action.id}`} style={{ backgroundColor: '#f9fafb' }}>
                      <Td colSpan="7">
                        <div style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <h3 style={{ marginBottom: '20px', color: '#1f2937' }}>Редактирование: {action.action_key}</h3>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '900px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Название (RU) *
                              </label>
                              <Input
                                type="text"
                                value={editData.display_name_ru || ''}
                                onChange={(e) => setEditData({...editData, display_name_ru: e.target.value})}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Название (EN)
                              </label>
                              <Input
                                type="text"
                                value={editData.display_name_en || ''}
                                onChange={(e) => setEditData({...editData, display_name_en: e.target.value})}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Провайдер
                              </label>
                              <Select
                                value={editData.provider_id || ''}
                                onChange={(e) => setEditData({...editData, provider_id: e.target.value ? parseInt(e.target.value) : null})}
                              >
                                <option value="">-- Не привязан --</option>
                                {getProvidersByType(action.type).map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </Select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#374151' }}>
                                Приоритет
                              </label>
                              <Input
                                type="number"
                                value={editData.priority || 0}
                                onChange={(e) => setEditData({...editData, priority: parseInt(e.target.value) || 0})}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', gridColumn: '1 / -1' }}>
                              <ToggleSwitch>
                                <input
                                  type="checkbox"
                                  checked={editData.enabled !== undefined ? editData.enabled : true}
                                  onChange={(e) => setEditData({...editData, enabled: e.target.checked})}
                                />
                                <span className="slider" />
                              </ToggleSwitch>
                              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Включен</span>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', gridColumn: '1 / -1', marginTop: '8px' }}>
                              <Button bg="#059669" onClick={() => handleSave(action.id)}>
                                <Save size={14} />
                                Сохранить
                              </Button>
                              <Button bg="#6b7280" onClick={handleCancel}>
                                Отмена
                              </Button>
                              <Button bg="#dc2626" onClick={() => handleDelete(action.id, action.action_key)}>
                                <Trash2 size={14} />
                                Удалить
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Td>
                    </tr>
                  ) : (
                    <tr key={action.id}>
                      <Td><code>{action.action_key}</code></Td>
                      <Td>{action.display_name}</Td>
                      <Td>
                        <Badge bg="#e0e7ff" color="#4f46e5">{action.type}</Badge>
                      </Td>
                      <Td>
                        {action.provider_name ? (
                          <span>{action.provider_name}</span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>— Не привязан</span>
                        )}
                      </Td>
                      <Td>{action.priority}</Td>
                      <Td>
                        {action.enabled ? (
                          <Badge bg="#d1fae5" color="#059669">
                            <Check size={14} />
                            Активен
                          </Badge>
                        ) : (
                          <Badge bg="#fee2e2" color="#dc2626">
                            <X size={14} />
                            Выключен
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button bg="#4f46e5" onClick={() => handleEdit(action)}>
                            <Edit size={14} />
                            Редактировать
                          </Button>
                          <Button bg="#dc2626" onClick={() => handleDelete(action.id, action.action_key)}>
                            <Trash2 size={14} />
                            Удалить
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  )
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <div style={{ marginTop: '16px', textAlign: 'center', color: '#6b7280' }}>
            Всего: {actions.length}
          </div>
        </>
      )}
    </Container>
  );
}

export default BotActions;

